import z from 'zod';

/** Sparse heat cell: `[binX, binY, eventCount]`. World coords ≈ cell * `bin_size`. */
export const mapHeatmapBinSchema = z.tuple([z.number(), z.number(), z.number()]);
export type MapHeatmapBin = z.infer<typeof mapHeatmapBinSchema>;

export const mapHeatmapClusterVideosSchema = z
    .object({
        total: z.number(),
        sample: z.array(z.string()),
    })
    .describe('MapHeatmapClusterVideos');
export type MapHeatmapClusterVideos = z.infer<typeof mapHeatmapClusterVideosSchema>;

export const mapHeatmapClusterSchema = z
    .object({
        x: z.number(),
        y: z.number(),
        /** Coverage radius in game-world units (varies per circle). */
        r: z.number(),
        /** Total events captured in this cluster. */
        events: z.number(),
        /**
         * Fraction of category total events (`0.52` = 52%).
         * Shares across circles intentionally sum to ~0.5 (median-distance coverage).
         */
        share: z.number(),
        videos: mapHeatmapClusterVideosSchema,
    })
    .describe('MapHeatmapCluster');
export type MapHeatmapCluster = z.infer<typeof mapHeatmapClusterSchema>;

export const mapHeatmapEventCategorySchema = z
    .object({
        event_types: z.array(z.string()),
        total_events: z.number(),
        peak_cell: z.number(),
        bins: z.array(mapHeatmapBinSchema),
        /** Weighted k-means circles, ordered by descending `events` (up to ~7). */
        clusters: z.array(mapHeatmapClusterSchema),
    })
    .describe('MapHeatmapEventCategory');
export type MapHeatmapEventCategory = z.infer<typeof mapHeatmapEventCategorySchema>;

export const mapHeatmapWindowSchema = z
    .object({
        days: z.number(),
        start: z.string(),
        end: z.string(),
    })
    .describe('MapHeatmapWindow');
export type MapHeatmapWindow = z.infer<typeof mapHeatmapWindowSchema>;

/**
 * GET `/v3/analytics/maps/:titleId/heatmap`
 * Category keys under `event_categories` are UI labels (e.g. `kills`, `deaths`).
 */
export const mapHeatmapResponseSchema = z
    .object({
        title_id: z.number(),
        window: mapHeatmapWindowSchema,
        generated_at: z.string(),
        bin_size: z.number(),
        world_to_deg: z.number(),
        video_sample_cap: z.number(),
        heat_bins_truncated: z.boolean(),
        clip_points_truncated: z.boolean(),
        event_categories: z.record(z.string(), mapHeatmapEventCategorySchema),
    })
    .describe('MapHeatmapResponse');
export type MapHeatmapResponse = z.infer<typeof mapHeatmapResponseSchema>;

export const mapHeatmapQuerySchema = z
    .object({
        eventTypes: z.array(z.string()).min(1),
        days: z.number().int().positive(),
    })
    .describe('MapHeatmapQuery');
export type MapHeatmapQuery = z.infer<typeof mapHeatmapQuerySchema>;
